import { useMutation, useQuery } from '@apollo/client';
import _ from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import Col from 'react-bootstrap/Col';
import Nav from 'react-bootstrap/Nav';
import Row from 'react-bootstrap/Row';
import { Tab } from 'react-bootstrap';
import { Redirect, Route, Switch, useHistory, useParams } from 'react-router-dom';
import { CREATE_COLLECTION, GET_COLLECTIONS } from './graphql';
import CreateModal from './components/CreateModal';
import CollectionView from './components/CollectionView';
import { LoadingLogo } from '../../../devtools/components/Modal';
import { Collection, GetBadgesQuery, GetItemsQuery } from '../../../../../__gqltypes__';
import { GET_ITEMS, GET_BADGES } from '../../components/apolloQueries';

function indexItems<T extends { id: string }>(list: T[]) {
  const indexedItems: Record<string, T> = {};

  list.forEach((item) => {
    indexedItems[item.id] = item;
  });

  return indexedItems;
}
const baseURL = '/sp3/liveops/collections';

const CollectionsView = <T extends string>({ type }: { type: T }) => {
  const { id: collectionId } = useParams<{ id: string }>();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [collections, setCollections] = useState<Record<string, Collection> | null>(null);
  const { data, loading, refetch } = useQuery(GET_COLLECTIONS, {
    variables: { limit: null, offset: null },
    fetchPolicy: 'no-cache',
  });

  const { data: vanityData } = useQuery<GetItemsQuery>(GET_ITEMS);
  const { data: badgesData } = useQuery<GetBadgesQuery>(GET_BADGES);
  const history = useHistory();

  useEffect(() => {
    if (data) {
      setCollections(indexItems(data?.liveOps?.collections?.list));
    }
  }, [data]);

  const amountOfCollections = data?.liveOps?.collections?.totalCount;

  const [createCollection, { loading: createLoading }] = useMutation(CREATE_COLLECTION, {
    onCompleted: () => refetch(),
  });

  const selectCollection = useCallback(
    (newCollectionId) => {
      if (Object.keys(collections ?? {}).includes(newCollectionId)) {
        history.push(`${baseURL}/${newCollectionId}`);
      }
    },
    [collections, history]
  );

  const isCollectionSelected = useCallback(() => {
    return !_.isEmpty(collectionId) && collections && Object.values(collections).find(({ id }) => id === collectionId);
  }, [collectionId, collections]);

  useEffect(() => {
    if (collectionId === ':id') {
      selectCollection('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCollectionSelected, selectCollection, collectionId]);

  const handleCreate = (name: string, type_: string) => {
    createCollection({
      variables: {
        input: {
          name,
          type: type_,
        },
      },
    })
      .then(({ data: data_ }) => {
        setShowCreateModal(false);
        refetch().then(() => history.push(`/sp3/liveops/collections/${data_.createCollection.id}`));
      })
      .catch(() => {
        alert('Something went wrong :(');
      });
  };

  return (
    <>
      <LoadingLogo show={loading || createLoading} />
      <CreateModal
        show={(!collections || amountOfCollections === 0 || showCreateModal) && !loading}
        loading={loading || createLoading}
        onHide={() => setShowCreateModal(false)}
        onAdd={handleCreate}
      />
      {collections && (
        <Tab.Container activeKey={collectionId} onSelect={(e) => selectCollection(e)}>
          <Row className="d-flex">
            <Col>
              <h1 className="text-center">{type}</h1>
              <Button className="w-100 mb-3" variant="success" onClick={() => setShowCreateModal(true)}>
                Add a new {type}
              </Button>
              <Nav variant="pills" className="mb-3" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
                {Object.values(collections).map((item) => (
                  <Nav.Item key={item.id} style={{ width: '100%' }} className="text-center">
                    <Nav.Link eventKey={item.id}>{item.name}</Nav.Link>
                  </Nav.Item>
                ))}
              </Nav>
            </Col>
            <Col className="col-10">
              <Switch>
                {collections && (
                  <Route exact path="/sp3/liveops/collections">
                    <Redirect to={`/sp3/liveops/collections/${Object.keys(collections)[0]}`} />
                  </Route>
                )}

                <Route
                  path="/sp3/liveops/collections/:id"
                  render={({ match: _match }) => {
                    return (
                      !!amountOfCollections && (
                        <CollectionView
                          badgesData={badgesData}
                          vanityData={vanityData}
                          collection={collections[_match.params.id]}
                          refetch={refetch}
                        />
                      )
                    );
                  }}
                />
              </Switch>
            </Col>
          </Row>
        </Tab.Container>
      )}
    </>
  );
};

export default CollectionsView;
